import { TrendingUp } from "lucide-react";
import { useTrend } from "../lib/trend";

/**
 * TrendButton — small "View trend" chip that opens the Trend Analyzer.
 * Renders next to any widget's header. Prevents click bubbling so the
 * outer widget button (drill-down) is NOT triggered.
 *
 * Props:
 *   widgetId  stable id, e.g. "overview-total-assets"
 *   label     display name shown in the analyzer header
 *   module    "overview" | "control" | ... (context breadcrumb)
 *   unit      "count" | "percent" | "duration" | "currency"
 *   current   current numeric value (for realistic mock)
 *   size      "sm" | "md"
 *   testid    optional data-testid override
 */
export default function TrendButton({
  widgetId,
  label,
  module,
  unit = "count",
  current = 0,
  size = "sm",
  testid,
}) {
  const { openTrend } = useTrend();
  if (!widgetId) return null;

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openTrend({ widgetId, label, module, unit, current });
  };

  const pad =
    size === "md"
      ? "px-2.5 py-1 text-[11.5px]"
      : "px-2 py-[3px] text-[10.5px]";

  return (
    <button
      type="button"
      data-testid={testid || `trend-btn-${widgetId}`}
      onClick={stop}
      className={`inline-flex items-center gap-1.5 rounded-full font-bold small-caps tracking-[0.06em] transition-colors ${pad}`}
      style={{
        background: "rgba(213,88,22,0.10)",
        border: "1px solid rgba(213,88,22,0.32)",
        color: "var(--cv-orange)",
        letterSpacing: "0.06em",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(213,88,22,0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(213,88,22,0.10)";
      }}
      aria-label={`View trend for ${label || widgetId}`}
    >
      <TrendingUp className={size === "md" ? "w-3.5 h-3.5" : "w-3 h-3"} strokeWidth={2.6} />
      View trend
    </button>
  );
}
